import { applyStyles, getButtonStyles, getContainerStyles, getIframeStyles } from "./styles";
import type { WidgetConfig, WidgetElements, WidgetState } from "./types";

export namespace AIChatWidget {

  export const defaultConfig: Omit<WidgetConfig, "iframeUrl"> = {
    maxWidth: '375px',
    height: '667px',
    buttonSize: '60px',
    buttonColor: '#007bff',
    buttonPosition: { bottom: '20px', right: '20px' },
    zIndex: 9999,
    mobileBreakpoint: 480,
  };

  // Pure functions for creating elements
  const createButton = (config: WidgetConfig, onToggle: () => void): HTMLDivElement => {
    const button = document.createElement('div');
    button.id = 'ai-chat-button';
    button.setAttribute('role', 'button');
    button.setAttribute('aria-label', 'Open chat widget');

    // Chat icon SVG
    button.innerHTML = `
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
    </svg>
  `;

    applyStyles(button, getButtonStyles(config));

    const handleHover = (isHover: boolean) => {
      if (isHover) {
        button.style.transform = 'scale(1.1)';
        button.style.boxShadow = '0 6px 16px rgba(0,0,0,0.2)';
      } else {
        button.style.transform = 'scale(1)';
        button.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
      }
    };

    button.addEventListener('mouseenter', () => handleHover(true));
    button.addEventListener('mouseleave', () => handleHover(false));
    button.addEventListener('click', onToggle);

    return button;
  };

  const createContainer = (config: WidgetConfig): HTMLDivElement => {
    const container = document.createElement('div');
    container.id = 'ai-chat-container';
    container.setAttribute('role', 'dialog');
    container.setAttribute('aria-label', 'Chat widget');

    applyStyles(container, getContainerStyles(config));

    return container;
  };

  const createIframe = (config: WidgetConfig): HTMLIFrameElement => {
    const iframe = document.createElement('iframe');
    iframe.src = config.iframeUrl;
    iframe.id = 'ai-chat-iframe';
    iframe.title = 'AI Chat Widget';
    iframe.setAttribute('allow', 'microphone; camera');

    applyStyles(iframe, getIframeStyles());

    return iframe;
  };

  // Pure function for updating button icon
  const updateButtonIcon = (button: HTMLDivElement, isOpen: boolean): void => {
    if (isOpen) {
      // Close icon (X)
      button.innerHTML = `
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <line x1="18" y1="6" x2="6" y2="18"></line>
        <line x1="6" y1="6" x2="18" y2="18"></line>
      </svg>
    `;
      button.setAttribute('aria-label', 'Close chat widget');
    } else {
      // Chat icon
      button.innerHTML = `
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
      </svg>
    `;
      button.setAttribute('aria-label', 'Open chat widget');
    }
  };

  // Pure function for mobile adjustments
  const adjustForMobile = (elements: WidgetElements, config: WidgetConfig): void => {
    const isMobile = window.innerWidth < (config.mobileBreakpoint || 480);

    if (isMobile) {
      elements.container.style.right = '10px';
      elements.container.style.bottom = '80px';
      elements.container.style.maxWidth = 'calc(100% - 20px)';
      elements.button.style.right = '10px';
    } else {
      elements.container.style.right = config.buttonPosition?.right || '20px';
      elements.container.style.bottom = '90px';
      elements.container.style.maxWidth = config.maxWidth || '375px';
      elements.button.style.right = config.buttonPosition?.right || '20px';
    }
  };

  // Pure function for handling messages
  const createMessageHandler = (config: WidgetConfig, close: () => void) =>
    (event: MessageEvent): void => {
      // Verify origin for security
      try {
        const iframeOrigin = new URL(config.iframeUrl).origin;
        if (event.origin !== iframeOrigin) {
          return;
        }
      } catch (error) {
        console.error('Invalid iframe URL:', error);
        return;
      }

      // Handle messages from your chat widget
      if (event.data.type === 'CLOSE_WIDGET') {
        close();
      }
    };

  // Pure function for opening widget
  const openWidget = (elements: WidgetElements, config: WidgetConfig): void => {
    elements.container.style.display = 'block';

    setTimeout(() => {
      elements.container.style.opacity = '1';
      elements.container.style.transform = 'translateY(0)';
    }, 10);

    updateButtonIcon(elements.button, true);

    // Send message to iframe
    elements.iframe.contentWindow?.postMessage(
      { type: 'WIDGET_OPENED' },
      config.iframeUrl
    );
  };

  // Pure function for closing widget
  const closeWidget = (elements: WidgetElements, config: WidgetConfig): void => {
    elements.container.style.opacity = '0';
    elements.container.style.transform = 'translateY(20px)';

    setTimeout(() => {
      elements.container.style.display = 'none';
    }, 300);

    updateButtonIcon(elements.button, false);

    // Send message to iframe
    elements.iframe.contentWindow?.postMessage(
      { type: 'WIDGET_CLOSED' },
      config.iframeUrl
    );
  };

  // Pure function for destroying widget
  const destroyWidget = (elements: WidgetElements | null): void => {
    if (elements) {
      elements.button.remove();
      elements.container.remove();
    }
  };

  // Pure function for merging config with defaults
  const mergeConfig = (userConfig: WidgetConfig): WidgetConfig => ({
    maxWidth: '375px',
    height: '667px',
    buttonSize: '60px',
    buttonColor: '#007bff',
    buttonPosition: { bottom: '20px', right: '20px' },
    zIndex: 9999,
    mobileBreakpoint: 480,
    ...userConfig
  });

  // Main widget creation function
  export const createAIChatWidget = (userConfig: WidgetConfig) => {
    const config = mergeConfig(userConfig);
    let state: WidgetState = {
      isOpen: false,
      isInitialized: false
    };
    let elements: WidgetElements | null = null;

    // Toggle function
    const toggle = (): void => {
      if (state.isOpen) {
        close();
      } else {
        open();
      }
    };

    // Open function
    const open = (): void => {
      if (!elements || state.isOpen) return;

      state = { ...state, isOpen: true };
      openWidget(elements, config);
    };

    // Close function
    const close = (): void => {
      if (!elements || !state.isOpen) return;

      state = { ...state, isOpen: false };
      closeWidget(elements, config);
    };

    // Destroy function
    const destroy = (): void => {
      destroyWidget(elements);
      elements = null;
      state = { ...state, isInitialized: false };
    };

    // Render function
    const render = (): void => {
      const button = createButton(config, toggle);
      const container = createContainer(config);
      const iframe = createIframe(config);

      container.appendChild(iframe);
      document.body.appendChild(button);
      document.body.appendChild(container);

      elements = { button, container, iframe };
      state = { ...state, isInitialized: true };

      adjustForMobile(elements, config);

      const resizeHandler = () => {
        if (elements) adjustForMobile(elements, config);
      };
      window.addEventListener('resize', resizeHandler);

      // Listen for messages from iframe
      const messageHandler = createMessageHandler(config, close);
      window.addEventListener('message', messageHandler);
    };

    // Initialize function
    const init = (): void => {
      if (state.isInitialized) {
        console.warn('AI Chat Widget already initialized');
        return;
      }

      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', render);
      } else {
        render();
      }
    };

    // Auto-initialize
    init();

    // Return public API
    return {
      open,
      close,
      toggle,
      destroy,
      getState: () => ({ ...state }),
      getConfig: () => ({ ...config })
    };
  };
}

